import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, FormHelperText, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { TimeField } from "@mui/x-date-pickers/TimeField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Address2, DestinationTimingKbn } from "../../../assets/data/Constants";
import TkSelectForm from "../../../components/atoms/TkSelectForm";
import TkPrefecturesPick from "../../../components/molecules/TkPrefecturesPick";
import TkInputWithText from "../../../components/molecules/TkInputWithText";
import TkCorporateSearchModal from "../../../components/molecules/TkCorporateSearchModal";
import TkButton from "../../../components/atoms/TkButton";
import TkLabelText from "../../../components/atoms/TkLabelText";
import TkDatePicker from "../../../components/atoms/TkDatePicker";
import { TkGetCodeList } from "../../../utils/TkGetCode";
import { httpAPI_CON001 } from "../api/request";
import { GlobalContext } from "../../../context/globalContext";
import "./_style.scss";

/**
 * 宛先２　既存の宛先を選択
 * @param {any} props.onChange 値変更時のイベント
 * @param {any} props.defaultValue 初期値
 * @return AddressSelection
 */
const AddressSelection = (props: any) => {
  const { onChange, defaultValue, ...rest } = props;
  const [isPrefecture, setIsPrefecture] = useState(false);
  const selectedItem = useRef("");
  const prefectures = useRef([]);

  useEffect(() => {
    const returnValue = {
      select: defaultValue?.select,
      prefectures: defaultValue?.prefectures,
    };
    onChange && onChange(returnValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  const handleChange = (e: any, type: "select" | "prefectures") => {
    switch (type) {
      case "select":
        setIsPrefecture(
          e.target.value ===
            Address2["都道府県（施設の所在地）単位の共済契約者"]
        );
        selectedItem.current = e.target.value;
        break;
      case "prefectures":
        prefectures.current = e;
        break;
    }

    const returnValue = {
      select: selectedItem.current,
      prefectures: prefectures.current?.map((item) => {
        return { address2PrefectureCd: item };
      }),
    };

    onChange && onChange(returnValue);
  };

  return (
    <Box className="SCRW140002Components" data-testid="addressSelection">
      <Box className="flexBox">
        <TkSelectForm
          selectPropsList={TkGetCodeList(Address2)}
          onChange={(e) => handleChange(e, "select")}
          wantToSetValue={defaultValue?.select}
        />
        {isPrefecture && (
          <TkPrefecturesPick
            onSuccessful={(e) => handleChange(e, "prefectures")}
          />
        )}
      </Box>
      {rest.error && <FormHelperText error>{rest.errorMessage}</FormHelperText>}
    </Box>
  );
};

/**
 * 法人検索部品
 * @param {any} props.onChange 値変更時のイベント
 * @param {any} props.defaultValue 初期値
 * @return CorprateSearch
 */
const CorporateSearch = (props: any) => {
  const { onChange, defaultValue, ...rest } = props;

  const navigate = useNavigate();
  const { state, setState } = useContext(GlobalContext);
  const [open, setOpen] = useState(false);
  const [corpList, setCorpList] = useState<any>([]);
  const [selectedItem, setSelectedItem] = useState<any>("");

  useEffect(() => {
    let contractorList = defaultValue?.contractorList;

    const item = `${contractorList?.address2ContractorNo}:${contractorList?.address2CorpName} ${contractorList?.address2Mail}`;
    contractorList && setSelectedItem(item);

    const returnValue = {
      contractorNo: contractorList?.address2ContractorNo,
      corpName: contractorList?.address2CorpName,
      contactEmail: contractorList?.address2Mail,
    };

    onChange && onChange(returnValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  const handleCallApi = async (e?: any) => {
    // 契約者一覧参照API呼び出し
    try {
      const res = await httpAPI_CON001({
        contractorNo: e?.contractorNo,
        corpName: e?.corpName,
        prefectureCd: e?.prefectureCd,
      });

      if (res.resultCd === "I0000") {
        setCorpList(res.contractorList);
        setOpen(true);

        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  // 選択
  const handleSave = (e: any) => {
    const item = `${e?.contractorNo}:${e?.corpName} ${e?.contactEmail}`;
    e && setSelectedItem(item);

    onChange && onChange(e);
    setOpen(false);
  };

  return (
    <Box className="SCRW140002Components" data-testid="corporateSearch">
      <Box className="flexBox">
        <TkLabelText
          variant="standard"
          wantToSetValue={selectedItem}
          sx={{ width: "50%" }}
          disabled
        />
        <TkButton onClick={handleCallApi}>法人検索</TkButton>
        <TkCorporateSearchModal
          open={open}
          onClose={() => setOpen(false)}
          onSave={handleSave}
          onChangeSearchData={handleCallApi}
          listData={corpList}
        />
      </Box>
      {rest.error && <FormHelperText error>{rest.errorMessage}</FormHelperText>}
    </Box>
  );
};

/**
 * 宛先２
 * @param {any} props.type 1:既存の宛先を設定 2:個別に宛先を設定
 * @param {any} props.onChange 値変更時のイベント
 * @param {any} props.defaultValue 初期値
 * @return Address2Component
 */
const Address2Component = (props: any) => {
  const { type, onChange, defaultValue } = props;

  if (type === "1") {
    return <AddressSelection onChange={onChange} defaultValue={defaultValue} />;
  } else {
    return <CorporateSearch onChange={onChange} defaultValue={defaultValue} />;
  }
};

/**
 * 送信タイミング２
 * @param {any} props.type 1:月次 2:四半期 3:年次 4:個別に設定
 * @param {any} props.onChange 値変更時のイベント
 * @param {any} props.defaultValue 初期値
 * @return DestinationTiming2
 */
const DestinationTiming2 = (props: any) => {
  const { type, onChange, defaultValue } = props;
  const month = useRef("");
  const day = useRef("");
  const date = useRef("");

  useEffect(() => {
    onChange && onChange(defaultValue ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  const handleChange = (e: any, type: "month" | "day" | "date") => {
    switch (type) {
      case "month":
        month.current = e.target.value;
        break;
      case "day":
        day.current = e.target.value;
        break;
      case "date":
        date.current = e.target.value;
        break;
    }

    const returnValue = {
      month: month.current,
      day: day.current,
      date: date.current,
    };

    onChange && onChange(returnValue);
  };

  return (
    <Box className="SCRW140002Components" data-testid="destinationTiming2">
      <Box className="flexBox">
        {type !== DestinationTimingKbn["個別に指定"] ? (
          <>
            {(type === DestinationTimingKbn["四半期"] ||
              type === DestinationTimingKbn["年次"]) && (
              <TkInputWithText
                innerText="月"
                inputProps={{ maxLength: 2 }}
                onlyNumber
                wantToSetValue={defaultValue?.month}
                onChange={(e: any) => handleChange(e, "month")}
              />
            )}
            <TkInputWithText
              innerText="日"
              inputProps={{ maxLength: 2 }}
              onlyNumber
              wantToSetValue={defaultValue?.day}
              onChange={(e: any) => handleChange(e, "day")}
            />
            {type === DestinationTimingKbn["四半期"] && (
              <Typography
                sx={{
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                から３ヶ月毎
              </Typography>
            )}
          </>
        ) : (
          <TkDatePicker
            onChange={(e: any) => handleChange(e, "date")}
            wantToSetValue={defaultValue?.date}
            maxDate={new Date("2099/12/31")}
            minDate={new Date()}
          />
        )}
      </Box>
    </Box>
  );
};

/**
 * 送信時刻
 * @param {any} props.onChange 値変更時のイベント
 * @param {any} props.defaultValue 初期値
 * @return SendBegIntegerime
 */
const SendBegIntegerime = (props: any) => {
  const { onChange, defaultValue } = props;
  const [value, setValue] = useState<any>(null);

  useEffect(() => {
    defaultValue && setValue(`0000-00-00T${defaultValue}`);

    onChange && onChange(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  const handleChange = (e: any) => {
    onChange && onChange(new Date(e).toLocaleTimeString());
  };

  return (
    <Box data-testid="sendBegIntegerime">
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        localeText={{
          fieldHoursPlaceholder: () => "00",
          fieldMinutesPlaceholder: () => "00",
        }}
      >
        <DemoContainer components={["TimeField"]}>
          <TimeField
            label="送信時刻"
            format="HH:mm"
            onChange={handleChange}
            value={value ? dayjs(value) : null}
          />
        </DemoContainer>
      </LocalizationProvider>
    </Box>
  );
};

export {
  AddressSelection,
  CorporateSearch,
  Address2Component,
  DestinationTiming2,
  SendBegIntegerime,
};
